import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { apiGet, apiPost } from 'tennders-stracture/libs/api'
import useSocket from 'tennders-common/hooks/useSocket'
import { StateType } from 'tennders-common/types/StateType'
import { useToast } from 'tennders-common/hooks/useToast'
import { dispatchCarriers, resetSelectedRows } from 'tennders-common/stores/data/action'
import { dispatchMultiEditMode, dispatchMultiSelectMode } from 'tennders-common/stores/view/action'
import useTranslation from 'next-translate/useTranslation'

type Props = {
  modal?: {
    className ?:string,
    withForm ?: boolean,
    singleTransaction ?: boolean,
    withoutMarketPlace?: boolean,
    onSelect ?: (params)=>void,
    onSelectMp ?: (params)=>void,
    transactionId ?: number,
    isSharedWithAll?: boolean
  },
  onClose ?: ()=>void,
}

const useShareLoadWithCarriers = ({ onClose, modal }: Props) => {
  const { withoutMarketPlace, withForm, onSelect, transactionId } = modal
  const [form, setForm] = useState<any>({})
  const [query, setQuery] = useState('')
  const [filtereds, setFiltereds] = useState([])
  const onChange = (k, v) => setForm({ ...form, [k]: v })
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const carriers = useSelector((state:StateType) => state.data.carriers)
  const selectedRows = useSelector((state: StateType) => state.data.selectedRows)
  const { toastSuccess, toastError } = useToast()
  const dispatch = useDispatch()
  const setCarriers = (carriers) => dispatch(dispatchCarriers(carriers))
  const setMultiSelectMode = (mode) => dispatch(dispatchMultiSelectMode(mode))
  const setEditMode = (mode) => dispatch(dispatchMultiEditMode(mode))
  const cancelSelectedRows = () => dispatch(resetSelectedRows())
  const { setRefresh } = useSocket()
  const { t } = useTranslation()

  const loadCarriers = useCallback(async () => {
    setLoading(true)
    const sharedWith = transactionId ? await apiGet(`/broker/${transactionId}/sharedWith`) : null
    const response = await apiGet('carrier/companies', { params: { attributes: '{id, name, users{id, name}}' } })
    if (response && Array.isArray(response)) {
      const allUsers = response.reduce((acc, obj) => [
        ...acc,
        ...obj.users.map((user) => ({
          ...user,
          name: `${user?.name} (${obj.name})`,
          companyId: obj.id
        }))
      ], [])

      if (sharedWith?.length > 0) {
        const sharedUserIds = new Set(sharedWith)

        const filteredUsers = allUsers.filter((user) => sharedUserIds.has(user.id))
        const unsharedUsers = allUsers.filter((user) => !sharedUserIds.has(user.id))

        setForm({ ...form, sharedUsers: filteredUsers })
        setCarriers(unsharedUsers)
      } else {
        setCarriers(allUsers)
      }
    }
    setLoading(false)
  }, [transactionId])

  const getUsersAndCompanies = (selectedCarriers) => {
    const companies = Array.from(new Set(selectedCarriers.map(obj => obj.companyId)))
    const users = selectedCarriers?.map((c) => c.value)

    return { companies, users }
  }

  const getTransactions = useCallback(() => {
    const transactions = []
    for (let i = 0; i < Object.keys(selectedRows).length; i += 1) {
      const key = Object.keys(selectedRows)[i]
      if (key.includes('transaction_') && selectedRows[key] === true) {
        transactions.push(key.replace(/transaction_/, ''))
      }
    }
    if (transactions?.length === 0) {
      return [transactionId]
    }
    return transactions
  }, [transactionId, selectedRows])

  const share = async (user?: any, unshare?: boolean, withAll?: boolean) => {
    let params = {}
    const transactions = getTransactions()
    if (!withAll) {
      const { companies, users } = getUsersAndCompanies(user ? [user] : form?.selectedCarriers)
      params = {
        transactions,
        companies,
        users: user ? [user.id] : users
      }
    } else {
      params = {
        transactions,
        withAll: true
      }
    }

    setSaving(true)
    const response = await apiPost(`broker/${unshare ? 'un' : ''}shareTransactions`, params)
    setSaving(false)
    if (response.error) {
      toastError(response.error)
      return
    }
    if (!user) {
      toastSuccess(t('modal:shared_successfully_message'))
      setEditMode(false)
      setMultiSelectMode(false)
      cancelSelectedRows()
      setRefresh('all')
      onClose()

      if (withAll) {
        setForm({ ...form, sharedUsers: [] })
      }
    } else {
      toastSuccess('Updated successfully')
      if (unshare) {
        const newForm = { ...form, sharedUsers: form.sharedUsers.filter((u) => u.id !== user.id) }
        setForm(newForm)
      }
    }
  }

  useEffect(() => {
    if (query.length === 0) {
      setFiltereds([])
    } else if (query?.length === 1) {
      setFiltereds(carriers)
    }
  }, [query, carriers])

  useEffect(() => {
    if (transactionId || modal) {
      loadCarriers()
    }
  }, [transactionId, modal])

  return {
    carriers: filtereds,
    loading,
    saving,
    query,
    share,
    setForm,
    onChange,
    filterBy: setQuery,
    withForm,
    form,
    withoutMarketPlace,
    transactionId
  }
}

export default useShareLoadWithCarriers
